import { useMutation, useQueryClient } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { ChatThread } from "types/ChatThread";
import type { Task } from "types/Task";
import { TASKS_QUERY_KEY } from "./useTasksQuery";
import { deleteThread } from "../methods/deleteThread";

/**
 * Deletes a chat thread and removes it from the cached threads and tasks lists.
 */
export default function useDeleteThreadMutation() {
  const getAccessToken = useGetAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: string) => deleteThread(threadId, getAccessToken),
    onSuccess: ({ id }) => {
      // Drop the deleted thread from the cached list directly. Avoiding
      // invalidate so a deletion doesn't trigger an extra GET /threads
      // while the user is mid-flow.
      queryClient.setQueryData<ChatThread[]>(["cc-db-api", "threads"], (prev) =>
        prev?.filter((t) => t.id !== id)
      );
      queryClient.removeQueries({
        queryKey: ["cc-db-api", "threads", id, "messages"],
      });
      // Evict tasks for the deleted thread; server cascade handles the durable delete.
      queryClient.setQueryData<Task[]>(TASKS_QUERY_KEY, (prev) =>
        prev?.filter((j) => j.threadId !== id)
      );
    },
  });
}
